import { AxiosResponse } from 'axios';
import { call, fork, put, select, take, takeEvery } from 'redux-saga/effects';

import {
  DataModelTransition,
  DataModelTransitionParams,
} from '../domain/dataModelTransition';
import { resolveModelTransitionMainField } from '../domain/modelTransition/mainField';
import { MAIN_FIELD_TYPE } from '../domain/schemas';
import { LoadingState } from '../domain/schemas';
import { SettingsOptionsModelTransition } from '../domain/settingsOptionsModelTransition';
import { ShuOption } from '../domain/shu';

import {
  DataModelTransitionActionCreators,
  DataModelTransitionActionTypes,
  FetchDataModelTransitionAction,
  dataModelTransitionSettingsSelector,
} from '../redux/server/dataModelTransition';
import {
  SettingsOptionsModelTransitionActionTypes,
  settingsOptionsModelTransitionLoadingStateSelector,
  settingsOptionsModelTransitionSelector,
} from '../redux/server/settingsOptionsModelTransition';
import {
  ModelTransitionReportsSettingActionCreators,
  modelTransitionReportsSelectedCurrentShuSelector,
  modelTransitionReportsSelectedShuSelector,
  modelTransitionTableHeaderSubMenuMultiFilterSelector,
  selectModelTransitionReportsCurrentShuAction,
  selectModelTransitionReportsShuAction,
} from '../redux/ui/modelTransitionReportsSetting';
import { Api, buildConfig } from '../utils/api';
import {
  convertShuOption,
  existShuOption,
  getShuOption,
  selectShus2HallReportSearchCondition,
} from '../utils/shu';
import {
  collectUniqueNonTransitionColumnValues,
  selectedValuesWithinOptions,
  typeMultiFilterSelectOptions,
} from '../utils/tableHeaderSubMenuMultiFilter';
import { handleErrorSaga } from './errorSaga';
import { changeColumnsOrderSaga } from './modelTransitionReportsSagas';

export function* fetchDataModelTransitionSaga(
  api: Api,
  action: FetchDataModelTransitionAction
) {
  try {
    yield put(
      DataModelTransitionActionCreators.fetchDataModelTransitionRequestAction()
    );

    const baseParams: DataModelTransitionParams = {
      ...action.payload.params,
      ...selectShus2HallReportSearchCondition([action.payload.shuOption]),
    };

    // 非推移項目が何も選択されなかった場合、mainField 列を明示的に追加する
    // これがなければデフォルトの非推移項目が表示されてしまうため
    const params =
      baseParams.nonTransitiveFields?.length === 0
        ? {
            ...baseParams,
            nonTransitiveFields: [
              resolveModelTransitionMainField(baseParams.mainField),
            ],
          }
        : baseParams;

    const response: AxiosResponse<DataModelTransition> = yield call(
      api.get,
      '/data/kiTransition',
      buildConfig(params)
    );

    yield put(
      DataModelTransitionActionCreators.fetchDataModelTransitionSuccessAction(
        response.data
      )
    );

    const settingsOptions: ReturnType<
      typeof settingsOptionsModelTransitionSelector
    > = yield select(settingsOptionsModelTransitionSelector);
    const sisTypeNames =
      settingsOptions.searchCondition.sisTypes?.map((item) => item.name) ?? [];
    const mainField = resolveModelTransitionMainField(
      response.data.setting?.mainField
    );
    const typeFilterIsHidden =
      mainField === MAIN_FIELD_TYPE.TYPE || mainField === MAIN_FIELD_TYPE.KITAG;
    const uniqueTypeValues = typeFilterIsHidden
      ? []
      : collectUniqueNonTransitionColumnValues(
          response.data.data?.columnsForNonTransition ?? [],
          response.data.data?.rows ?? [],
          'type'
        );
    const typeOptions = typeMultiFilterSelectOptions(
      sisTypeNames,
      uniqueTypeValues
    );
    yield put(
      ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsTableHeaderSubMenuMultiFilterSelectOptionsAction(
        { type: typeOptions }
      )
    );
    const currentFilter: ReturnType<
      typeof modelTransitionTableHeaderSubMenuMultiFilterSelector
    > = yield select(modelTransitionTableHeaderSubMenuMultiFilterSelector);
    const selectedType = currentFilter?.type ?? [];
    const nextType = selectedValuesWithinOptions(selectedType, typeOptions);
    if (selectedType.length !== nextType.length) {
      yield put(
        ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsTableHeaderSubMenuMultiFilterAction(
          { ...(currentFilter ?? {}), type: nextType }
        )
      );
    }

    yield fork(changeColumnsOrderSaga);
  } catch (error: unknown) {
    yield put(
      DataModelTransitionActionCreators.renewDataModelTransitionAction()
    );
    yield fork(handleErrorSaga, error);
  }
}

/**
 * リクエストした種別のデータとレスポンスの種別データが不一致だったときに、レスポンスの値で上書きする
 *
 * 種別の不一致は種別が削除されたとき、あるいは共有グループをまたいだお気に入りで発生する
 */
function* validateShuSaga() {
  const currentShu: ShuOption = yield select(
    modelTransitionReportsSelectedCurrentShuSelector
  );
  // currentShuが存在しない場合には初期リクエストになるため何もしない
  if (currentShu == null) {
    return;
  }

  const setting: DataModelTransitionParams = yield select(
    dataModelTransitionSettingsSelector
  );
  const exist = existShuOption(setting, currentShu);
  if (exist) {
    return;
  }

  const settingsOptionsLoadingState: LoadingState = yield select(
    settingsOptionsModelTransitionLoadingStateSelector
  );
  if (settingsOptionsLoadingState !== 'loaded') {
    yield take(
      SettingsOptionsModelTransitionActionTypes.FETCH_SETTINGS_OPTIONS_MODEL_TRANSITION_SUCCESS
    );
  }
  // FETCH_SETTINGS_OPTIONS_KI_SUCCESSを待つのでundefinedにならない
  const settingsOptions: SettingsOptionsModelTransition = yield select(
    settingsOptionsModelTransitionSelector
  );

  const shuOption =
    convertShuOption(setting, settingsOptions.searchCondition.shuGroupList) ??
    // 画面リンクを利用して存在しない場合は最初の値を選択する
    getShuOption(settingsOptions.searchCondition).at(0);

  if (shuOption == null) {
    throw new Error('適切な種別グループまたは種別が見つかりませんでした');
  }

  yield put(selectModelTransitionReportsCurrentShuAction(shuOption));

  const selectedShu: ShuOption[] = yield select(
    modelTransitionReportsSelectedShuSelector
  );

  // selectedShuの変更
  if (selectedShu.length > 0) {
    const validatedSelectedShu = [
      shuOption,
      ...selectedShu.filter((items) => items.code !== currentShu.code),
    ];
    // TODO: 変換後の重複の削除をする
    yield put(selectModelTransitionReportsShuAction(validatedSelectedShu));
  }
}

function* handleFetchDataModelTransitionSaga(api: Api) {
  yield takeEvery(
    DataModelTransitionActionTypes.FETCH_DATA_MODEL_TRANSITION,
    fetchDataModelTransitionSaga,
    api
  );

  yield takeEvery(
    DataModelTransitionActionTypes.FETCH_DATA_MODEL_TRANSITION_SUCCESS,
    validateShuSaga
  );
}

export function* dataModelTransitionSagas(context: { api: Api }) {
  yield fork(handleFetchDataModelTransitionSaga, context.api);
}
